import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import rawFleeceStockImg from "../assets/images/authentic/raw_wool_fleece_stock.webp";
import warehouseExteriorImg from "../assets/images/authentic/warehouse_exterior.webp";
import {
  Globe,
  ShieldCheck,
  Sliders,
  PackageCheck,
  ArrowRight,
  Phone,
  MessageCircle,
  Truck,
  Layers,
  Factory,
} from "lucide-react";

const RawWoolSupplierIndia = () => {
  const { lPath, isRTL } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Raw Wool Supplier in India",
        "item": "https://skwoolens.trivanahospitality.com/en/raw-wool-supplier-india"
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Raw Wool Supply in India",
    "provider": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "telephone": "+91-7014581250",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-26, Un Mandi, Antyodaya Nagar",
        "addressLocality": "Bikaner",
        "addressRegion": "Rajasthan",
        "postalCode": "334001",
        "addressCountry": "IN"
      }
    },
    "description": "Pan-India raw wool sourcing and bulk supply. Supplying greasy and scoured raw fleece (30+ and 32-34+ micron) to commercial spinning mills and yarn manufacturers across India.",
    "areaServed": {
      "@type": "Country",
      "name": "India"
    }
  };

  const schema = [breadcrumbSchema, serviceSchema];

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Raw Wool Supplier in India | S.K. Woollens — B2B Bulk Sourcing Since 1965"
        description="Trusted raw wool supplier in India. S.K. Woollens supplies specification-graded raw fleece, greasy bales & scoured wool (30+ & 32-34+ micron) to spinning mills nationwide."
        keywords="raw wool supplier in India, Indian wool traders, raw wool for spinning mills, bulk wool supplier in India, B2B wool sourcing in India, raw wool supply Rajasthan"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/raw-wool-supplier-india"
        schema={schema}
      />

      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/30 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="wool-badge inline-flex">
                <Globe className="w-3.5 h-3.5 text-wool-bronze" />
                <span>Pan-India B2B Wool Procurement</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
                Raw Wool Supplier in India
              </h1>
              <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
                Operating from Rajasthan's primary wool trading corridor, S.K. Woollens connects commercial spinning mills and carpet yarn manufacturers across India with dependable, specification-graded raw wool supplies.
              </p>

              <div className="p-4 bg-white/80 border border-wool-border rounded-xl text-xs sm:text-sm text-wool-slate">
                <span className="font-semibold text-wool-bronze block mb-1">
                  Commercial B2B Supply Reliability:
                </span>
                With six decades of wool trading expertise dating back to 1965 (Bulaki Chand Kesari Chand), we provide consistent lot grading, certified weights, and multi-tonne contract deliveries.
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <a
                  href="https://wa.me/917014581250?text=Hi%2C%20I%20am%20looking%20for%20a%20raw%20wool%20supplier%20in%20India%20for%20our%20spinning%20mill"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-300" />
                  <span>Enquire for Raw Wool Supply</span>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </a>

                <a href="tel:+917014581250" className="btn-secondary flex items-center gap-2">
                  <Phone className="w-4 h-4 text-wool-bronze" />
                  <span>Call +91 7014581250</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-wool-border bg-white">
                <img
                  src={rawFleeceStockImg}
                  alt="Raw Natural Wool Fleece Stock in Bikaner Godown - S.K. Woollens"
                  className="w-full h-72 sm:h-80 object-cover"
                  loading="eager"
                />
                <div className="p-3 bg-wool-sand/80 border-t border-wool-border text-center text-xs font-semibold text-wool-charcoal">
                  Graded Raw Wool Fleece Stock Ready for Mill Dispatch
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Domestic Wool Procurement Infrastructure */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <div className="max-w-3xl space-y-3">
            <div className="wool-badge">
              <Layers className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Sourcing Ecosystem</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              Systematic Domestic Sourcing Across Major Indian Wool Belts
            </h2>
            <p className="text-sm sm:text-base text-wool-slate leading-relaxed">
              India produces a diverse range of coarse and medium wool varieties prized globally for carpet weaving, felt manufacturing, and coarse industrial yarns. S.K. Woollens consolidates raw fleece from established regional mandis and direct pastoralist networks, grading every clip before commercial dispatch.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <ShieldCheck className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Pre-Purchase Lot Testing</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                We assess every fleece lot for fibre diameter, vegetable matter (VM) percentage, clean wool yield, and natural moisture to guarantee accurate billing weights.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Factory className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Spinning Mill Alignment</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                Whether your plant uses woolen cards, semi-worsted systems, or open-end spinning, we supply lots tailored to your machinery's draft and twist tolerances.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Truck className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Nationwide Mill Logistics</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                Full-truckload (FTL) and multi-tonne dispatch directly from Bikaner to textile clusters in Rajasthan, Haryana, Punjab, Uttar Pradesh, and Gujarat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Raw Wool Product Types Supplied */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-10">
          <div className="wool-badge">
            <Sliders className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Product Portfolio</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
            Raw Wool Types Available for Indian Mill Sourcing
          </h2>
          <p className="text-sm sm:text-base text-wool-muted">
            Supplying both greasy in-bale fleece and pre-scoured commercial lots to suit your mill's scouring infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-3">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Fleece Type 01</span>
            <h3 className="text-lg font-bold text-wool-charcoal">Greasy Raw Wool Bales</h3>
            <p className="text-xs text-wool-slate leading-relaxed">
              Unwashed, naturally greased fleece packed in dense, hydraulically pressed bales. Sourced directly from mandis with core test yield estimates for cost-effective in-house mill scouring.
            </p>
            <div className="pt-2 text-xs font-semibold text-wool-bronze-dark">
              Micron: 30+ & 32–34+ | Length: 50–100mm+
            </div>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-3">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Fleece Type 02</span>
            <h3 className="text-lg font-bold text-wool-charcoal">Pre-Scoured Wool Lots</h3>
            <p className="text-xs text-wool-slate leading-relaxed">
              Professionally washed, de-greased, and dried commercial wool lots. Ready for direct carding and spinning in mills without internal effluent or scouring facilities.
            </p>
            <div className="pt-2 text-xs font-semibold text-wool-bronze-dark">
              Clean Yield: High Commercial Clean % | Low Residual Grease
            </div>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-6 shadow-sm space-y-3">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Fleece Type 03</span>
            <h3 className="text-lg font-bold text-wool-charcoal">Carpet Blend Specialty Lots</h3>
            <p className="text-xs text-wool-slate leading-relaxed">
              Selected coarse clips offering high crimp elasticity and bulk, formulated to blend with New Zealand or regional wools for maximum yarn strength and pile recovery.
            </p>
            <div className="pt-2 text-xs font-semibold text-wool-bronze-dark">
              Application: Hand-Knotted, Tufted & Woven Carpet Yarn
            </div>
          </div>
        </div>
      </section>

      {/* 4. Warehouse & Dispatch Infrastructure */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-wool-charcoal text-white rounded-3xl p-6 sm:p-12 shadow-2xl">
          <div className="lg:col-span-6 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 text-wool-sand text-xs font-bold uppercase tracking-wider rounded-full">
              <PackageCheck className="w-3.5 h-3.5 text-wool-sand" />
              <span>Commercial Infrastructure</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">
              Commercial Godowns & Secure Loading Bays
            </h2>
            <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
              Our Bikaner warehouse infrastructure is engineered for rapid truck loading, moisture-safe storage, and verified weighing. We maintain buffer inventories of standard coarse grades to shield contract customers against spot market supply crunches.
            </p>
            <div className="grid grid-cols-2 gap-4 pt-2 text-xs">
              <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
                <span className="font-bold text-white block text-sm">60+ Years</span>
                <span className="text-wool-sand/70 text-[11px]">Continuous Market Standing</span>
              </div>
              <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
                <span className="font-bold text-white block text-sm">Pan-India</span>
                <span className="text-wool-sand/70 text-[11px]">Direct Mill Gate Dispatch</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative rounded-2xl overflow-hidden border border-white/20">
            <img
              src={warehouseExteriorImg}
              alt="S.K. Woollens Warehouse Exterior Facility in Bikaner"
              className="w-full h-72 sm:h-80 object-cover"
              loading="lazy"
            />
            <div className="p-3 bg-black/60 backdrop-blur-md border-t border-white/20 text-center text-xs text-wool-sand">
              S.K. Woollens Warehouse Complex — Antyodaya Nagar, Bikaner
            </div>
          </div>
        </div>
      </section>

      {/* 5. Related Pages Navigation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-wool-border bg-white rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <h3 className="text-xl font-bold text-wool-charcoal font-display">
            Explore Related Solutions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to={lPath("/wool-supplier-bikaner")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Wool Supplier in Bikaner</h4>
                <p className="text-xs text-wool-muted mt-1">Direct mandi presence and local Rajasthani wool lots.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/carpet-wool-supplier-india")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Carpet Wool Supplier India</h4>
                <p className="text-xs text-wool-muted mt-1">High-crimp coarse fibres for carpet yarn spinners.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/for-yarn-manufacturers")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">For Yarn Manufacturers</h4>
                <p className="text-xs text-wool-muted mt-1">Spinning mill procurement tailored to technical parameters.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/bulk-wool-supply")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Bulk Wool Supply</h4>
                <p className="text-xs text-wool-muted mt-1">Multi-tonne mill supply contracts and buffer storage.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Direct Contact CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-wool-sand via-wool-sand-dark to-wool-sand border border-wool-border rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-premium">
          <div className="max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Request a Commercial Raw Wool Quotation
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate">
              Send us your required micron range, staple length, and monthly tonnage requirements for a prompt commercial assessment.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20request%20a%20quotation%20for%20raw%20wool%20supply%20in%20India"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4 text-emerald-300" />
              <span>WhatsApp Requirement Desk</span>
            </a>

            <a
              href="tel:+917014581250"
              className="btn-secondary flex items-center gap-2 bg-white"
            >
              <Phone className="w-4 h-4 text-wool-bronze" />
              <span>Call Nikhil Banthia: +91 7014581250</span>
            </a>

            <Link to={lPath("/contact")} className="btn-secondary bg-white">
              <span>View Contact Page</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RawWoolSupplierIndia;
